import { ref } from "vue";

/**
 * The Chromium install prompt.
 *
 * Chromium fires `beforeinstallprompt` once the app meets the installability
 * bar (https, manifest with 192+512 icons, a service worker with a fetch
 * handler) and the app is not already installed. The event MUST be captured
 * and re-fired from a user gesture — there is no way to ask for it later — so
 * the listener is registered at boot and the event parked here.
 *
 * Safari fires nothing: iOS installs through Share → "Add to Home Screen"
 * only. `canInstall` therefore stays false there, and the TopBar simply does
 * not offer the row.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;

export const canInstall = ref(false);

export function watchInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppress Chrome's own mini-infobar; the shell offers the row instead.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    canInstall.value = true;
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    canInstall.value = false;
  });
}

/** Re-fire the parked prompt. Must be called from a user gesture. */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferred) return "unavailable";
  const event = deferred;
  // Single-use: Chromium refuses a second `prompt()` on the same event.
  deferred = null;
  canInstall.value = false;
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return "dismissed";
  }
}

/** Test seam — clears the parked event between cases. */
export function resetInstallPrompt(): void {
  deferred = null;
  canInstall.value = false;
}
